import {SelectField, type SelectFieldOption} from '@wandb/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {type Maybe} from 'graphql/jsutils/Maybe';
import React, {type FC, useMemo, useState} from 'react';
import {type Key} from 'react-aria-components';
import {components, type OptionProps} from 'react-select';
import {Image} from 'semantic-ui-react';

import {useGetRoleOptions} from '../../../components/MembersTable/TeamMembersTable/useGetRoleOptions';
import {type UserAccountType} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {type TeamMember} from '../../../types/graphql';
import {checkIsServiceAccount} from '../../../util/serviceAccounts';

export interface UserInviteOption {
  value: string;
  label: string;
  username: Maybe<string>;
  photoUrl: Maybe<string>;
  name: Maybe<string>;
  email: Maybe<string>;
  accountType: Maybe<UserAccountType>;
}

interface UserInviteOptionsProps {
  users: TeamMember[];
  selectedUsers: string[];
  setSelectedUsers: (users: string[]) => void;
}

export const UserInviteOptions: FC<UserInviteOptionsProps> = ({
  users,
  selectedUsers,
  setSelectedUsers,
}) => {
  const viewer = useViewer();
  const [searchQuery, setSearchQuery] = useState('');

  const allItems: SelectFieldOption[] = useMemo(
    () =>
      users
        .filter(u => u.id && u.id !== viewer?.id)
        .map(u => {
          const username = u.username ? `@${u.username}` : '';
          const label = u.name
            ? username
              ? `${u.name} (${username})`
              : u.name
            : username || u.email || (u.id as string);
          return {
            id: u.id as string,
            label,
            description: u.email ?? undefined,
            decorationLeft: u.photoUrl
              ? {avatarProps: {src: u.photoUrl, alt: u.name ?? ''}}
              : undefined,
          };
        }),
    [users, viewer?.id]
  );

  const filteredItems = useMemo(() => {
    if (!searchQuery) {
      return allItems;
    }
    const query = searchQuery.toLowerCase();
    return allItems.filter(item => {
      const user = users.find(u => u.id === item.id);
      if (!user) {
        return false;
      }
      return (
        user.name?.toLowerCase().includes(query) ||
        user.username?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    });
  }, [allItems, searchQuery, users]);

  return (
    <SelectField
      aria-label="Invite team members"
      items={filteredItems}
      placeholder="Email or username"
      selectionMode="multiple"
      value={selectedUsers}
      onChange={keys => {
        setSelectedUsers((keys as Key[]).map(String));
      }}
      search={{value: searchQuery, onInput: setSearchQuery}}
      onOpenChange={isOpen => {
        if (!isOpen) {
          setSearchQuery('');
        }
      }}
      fullWidth
    />
  );
};

export const SingleUserInviteOptionComp: React.FC<
  OptionProps<UserInviteOption, false>
> = props => {
  const {data} = props;
  const {photoUrl, username, email, name} = data;

  return (
    <components.Option {...props}>
      <UserInviteOptionComp
        photoUrl={photoUrl}
        name={name}
        username={username}
        email={email}
      />
    </components.Option>
  );
};

export const UserInviteOptionComp: React.FC<
  Pick<UserInviteOption, 'photoUrl' | 'username' | 'email' | 'name'>
> = props => {
  const {photoUrl, username, email, name} = props;

  return (
    <Tailwind>
      <div className={'flex items-center'}>
        <Image ui avatar src={photoUrl} />
        <div className={'flex flex-col items-center'}>
          <div className={'self-start'}>
            <span>{name}</span>
            {username && (
              <span className={'ml-6 text-sm text-wb-text-tertiary'}>
                @{username}
              </span>
            )}
          </div>
          <span className={'-mt-2 self-start text-sm text-wb-text-tertiary'}>
            {email}
          </span>
        </div>
      </div>
    </Tailwind>
  );
};

interface UserRoleItemProps {
  user: TeamMember;
  isOwner?: boolean;
  entityName: string;
}

export const UserRoleItem: FC<UserRoleItemProps> = ({
  user,
  isOwner,
  entityName,
}) => {
  let userRole: string = user.memberRole?.name ?? '';
  const {roleOptions} = useGetRoleOptions(entityName);

  const viewer = useViewer();
  const userIsViewer = user.id === viewer?.id;

  const defaultRoleOption = roleOptions.find(
    roleOption => roleOption.value === userRole
  );
  const defaultRoleName = defaultRoleOption?.text;

  if (checkIsServiceAccount(user.accountType)) {
    userRole = 'Service Account';
  } else if (isOwner) {
    userRole = 'Owner';
  } else if (defaultRoleName) {
    userRole = defaultRoleName;
  }

  return (
    <Tailwind>
      <div className={'mt-12 flex items-center'}>
        <Image ui avatar src={user.photoUrl} className={'mr-12'} />
        <span>
          {user.name}
          {userIsViewer && ' (you)'}
        </span>
        <span className={'ml-auto'}>{userRole}</span>
      </div>
    </Tailwind>
  );
};
